import React, { useState } from 'react';
import { X, Star, Send, CheckCircle2, Copy, AlertCircle, Loader2 } from 'lucide-react';
import { feedbackAPI } from '../services/api';

const FeedbackModal = ({ isOpen, onClose, onSuccess }) => {
    const [step, setStep] = useState(1); // 1: Form, 2: Success
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [token, setToken] = useState('');
    const [copied, setCopied] = useState(false);

    const [formData, setFormData] = useState({
        customerName: '',
        customerEmail: '',
        feedbackMessage: '',
        rating: 5
    });

    // Reset state when modal opens
    React.useEffect(() => {
        if (isOpen) {
            setStep(1);
            setToken('');
            setCopied(false);
            setFormData({
                customerName: '',
                customerEmail: '',
                feedbackMessage: '',
                rating: 5
            });
        }
    }, [isOpen]);

    const handleReset = () => {
        setStep(1);
        setToken('');
        setFormData({
            customerName: '',
            customerEmail: '',
            feedbackMessage: '',
            rating: 5
        });
    };

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await feedbackAPI.submitFeedback(formData);
            setToken(response.data.publicToken);
            setStep(2);
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('Feedback submission error:', err);
            let errorMessage = err.response?.data?.message;

            // If message is generic or missing, and we have detailed errors
            if (!errorMessage && err.response?.data?.errors) {
                const errors = err.response.data.errors;
                if (Array.isArray(errors)) {
                    errorMessage = errors.map(e => e.defaultMessage || e.message || JSON.stringify(e)).join(', ');
                } else if (typeof errors === 'object') {
                    errorMessage = Object.values(errors).join(', ');
                }
            } else if (!errorMessage && err.response?.data?.error) {
                errorMessage = err.response.data.error;
            }

            setError(errorMessage || 'Failed to submit feedback. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(token);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-[#001524]/80 backdrop-blur-md transition-opacity"
                onClick={onClose}
            ></div>

            {/* Modal Content */}
            <div className="relative w-full max-w-xl bg-[#001e33] border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-full transition-all"
                >
                    <X className="h-6 w-6" />
                </button>

                {step === 1 ? (
                    <div className="p-8 md:p-12">
                        <div className="text-center mb-10">
                            <h2 className="text-3xl font-black text-white mb-2 tracking-tight">Share Your Experience</h2>
                            <p className="text-gray-400">Tell us how we did and help others make a better choice.</p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-500 text-sm">
                                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest pl-1">Your Name *</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="Shrikant Lavate"
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-sky-500/50 focus:bg-white/10 transition-all font-medium"
                                        value={formData.customerName}
                                        onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest pl-1">Email (Optional)</label>
                                    <input
                                        type="email"
                                        placeholder="jit@example.com"
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-sky-500/50 focus:bg-white/10 transition-all font-medium"
                                        value={formData.customerEmail}
                                        onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest pl-1">Your Rating</label>
                                <div className="flex gap-4 items-center bg-white/5 p-4 rounded-2xl border border-white/10">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, rating: star })}
                                            className="focus:outline-none transition-transform hover:scale-125 active:scale-95"
                                        >
                                            <Star
                                                className={`h-8 w-8 ${star <= formData.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700'}`}
                                            />
                                        </button>
                                    ))}
                                    <span className="ml-auto font-black text-2xl text-white">{formData.rating}/5</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest pl-1">Review Message *</label>
                                <textarea
                                    required
                                    rows="4"
                                    placeholder="Write your experience here..."
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:border-sky-500/50 focus:bg-white/10 transition-all font-medium resize-none"
                                    value={formData.feedbackMessage}
                                    onChange={(e) => setFormData({ ...formData, feedbackMessage: e.target.value })}
                                ></textarea>
                            </div>

                            <button
                                disabled={loading}
                                type="submit"
                                className="w-full bg-sky-500 hover:bg-sky-400 disabled:bg-sky-500/50 text-white font-black py-4 rounded-2xl transition-all shadow-xl flex items-center justify-center gap-3 active:scale-[0.98]"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-5 w-5" />
                                        Submit Feedback
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-500/10 border-2 border-green-500/20 mb-8">
                            <CheckCircle2 className="h-12 w-12 text-green-500" />
                        </div>
                        <h2 className="text-3xl font-black text-white mb-4">Feedback Received!</h2>
                        <p className="text-gray-400 mb-10 max-w-sm mx-auto">
                            Thank you for your valuable feedback. It will be visible on our page once approved by our team.
                        </p>

                        <div className="bg-sky-500/5 border border-white/10 rounded-[2rem] p-8 mb-10 text-left">
                            <label className="text-[10px] font-black text-sky-400 uppercase tracking-[0.2em] mb-4 block">Your Security Token</label>
                            <div className="flex items-center gap-4 bg-[#001524] rounded-2xl p-4 border border-white/5">
                                <code className="flex-1 text-sky-300 font-mono text-sm break-all">{token}</code>
                                <button
                                    onClick={copyToClipboard}
                                    className="p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-all text-white active:scale-90 flex-shrink-0"
                                    title="Copy to clipboard"
                                >
                                    {copied ? <CheckCircle2 className="h-5 w-5 text-green-400" /> : <Copy className="h-5 w-5" />}
                                </button>
                            </div>
                            <p className="mt-4 text-[10px] text-gray-500 font-medium leading-relaxed italic">
                                * SAVE THIS TOKEN. You will need it if you want to update or delete your feedback later.
                            </p>
                        </div>

                        <div className="flex flex-col gap-3">
                            <button
                                onClick={onClose}
                                className="w-full bg-white/5 hover:bg-white/10 text-white font-black py-4 rounded-2xl transition-all border border-white/10"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleReset}
                                className="w-full text-sky-400 hover:text-sky-300 font-bold py-2 rounded-2xl transition-all text-sm uppercase tracking-widest"
                            >
                                Submit Another Response
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeedbackModal;
